<section aria-labelledby="inventory-heading" class="w-full">
  <div class="flex items-center justify-between md:pt-4">
    <div class="flex items-center gap-2">
      <mat-icon aria-hidden="true">directions_run</mat-icon>
      <h1 id="inventory-heading" class="flex items-center gap-2">
        Services
        @if (isLoading) {
          <span
            class="font-lato inline-flex h-6 min-w-6 animate-pulse items-center justify-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700"
            aria-hidden="true"
          ></span>
        } @else {
          <span
            class="font-lato inline-flex min-w-6 items-center justify-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700"
            [attr.aria-label]="visibleServiceCount + ' services total'"
            >{{ visibleServiceCount | number }}</span
          >
        }
      </h1>
    </div>
    @if (services.length > 0) {
      <div class="flex items-center gap-2">
        <app-csv-download
          class="md:hidden"
          [iconOnly]="true"
          [compact]="true"
          [disabled]="isLoading || isServicesInProgress"
          [csvData]="formattedCSVData"
          [fileName]="'ServicesInventory.csv'"
          ariaLabel="Export services"
        ></app-csv-download>
        <app-csv-download
          class="hidden md:block"
          [compact]="true"
          [disabled]="isLoading || isServicesInProgress"
          [csvData]="formattedCSVData"
          [fileName]="'ServicesInventory.csv'"
          ariaLabel="Export services"
        ></app-csv-download>
      </div>
    }
  </div>
  @if (services.length > 0) {
    <div class="flex pt-4 pb-2">
      <div class="relative w-full">
        <label for="inventory-search" class="sr-only">Search services</label>
        <mat-icon
          class="pointer-events-none absolute top-1/2 left-3 flex! h-4! w-4! -translate-y-1/2 items-center! justify-center! text-lg! text-black"
          aria-hidden="true"
          >search</mat-icon
        >
        <input
          id="inventory-search"
          type="search"
          class="comcast-input mt-0! pl-9 text-sm!"
          [formControl]="searchTermControl"
          placeholder="Search (Ex: SD-WAN)"
          autocomplete="off"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-2 pb-4">
      <div
        class="lg2:flex lg2:w-auto lg2:flex-wrap lg2:items-center grid w-full grid-cols-2 gap-2"
      >
        <!-- Group by -->
        <div class="relative min-w-0">
          <button
            type="button"
            id="inventory-group-by-btn"
            class="font-lato lg2:w-auto inline-flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            [ngClass]="
              isGroupByDropdownOpen()
                ? 'border-blue-400 bg-white text-black'
                : 'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50'
            "
            [attr.aria-expanded]="isGroupByDropdownOpen()"
            aria-controls="inventory-group-by-options"
            aria-haspopup="listbox"
            (click)="toggleGroupByDropdown($event)"
            (keydown.escape)="closeFilterDropdowns()"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >folder_open</mat-icon
            >
            <span class="min-w-0 truncate">
              Group: <strong>{{ currentGroupByLabel }}</strong>
            </span>
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isGroupByDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isGroupByDropdownOpen()) {
            <div
              id="inventory-group-by-options"
              class="comcast-select-panel min-w-48"
              [class.comcast-select-panel--open]="isGroupByDropdownOpen()"
              role="listbox"
              aria-label="Group by options"
              (click)="$event.stopPropagation()"
              (keydown.escape)="closeFilterDropdowns()"
            >
              <div class="comcast-select-options">
                @for (option of groupByOptions; track option) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      currentGroupByLabel === option
                    "
                    role="option"
                    [attr.aria-selected]="currentGroupByLabel === option"
                    (click)="selectGroupBy(option)"
                  >
                    {{ option }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Sort -->
        <div class="relative min-w-0">
          <button
            type="button"
            id="inventory-sort-btn"
            class="font-lato lg2:w-auto inline-flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            [ngClass]="
              isSortDropdownOpen()
                ? 'border-blue-400 bg-white text-black'
                : 'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50'
            "
            [attr.aria-expanded]="isSortDropdownOpen()"
            aria-controls="inventory-sort-options"
            aria-haspopup="listbox"
            (click)="toggleSortDropdown($event)"
            (keydown.escape)="closeFilterDropdowns()"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >sort</mat-icon
            >
            <span class="min-w-0 truncate">
              Sort: <strong>{{ currentSortLabel }}</strong>
            </span>
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isSortDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isSortDropdownOpen()) {
            <div
              id="inventory-sort-options"
              class="comcast-select-panel min-w-40"
              [class.comcast-select-panel--open]="isSortDropdownOpen()"
              role="listbox"
              aria-label="Sort options"
              (click)="$event.stopPropagation()"
              (keydown.escape)="closeFilterDropdowns()"
            >
              <div class="comcast-select-options">
                @for (option of sortOptions; track option.label) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      isAscFormControl.value === option.value
                    "
                    role="option"
                    [attr.aria-selected]="
                      isAscFormControl.value === option.value
                    "
                    (click)="selectSort(option.value)"
                  >
                    {{ option.label }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Locations -->
        <div class="relative col-span-2 min-w-0 sm:col-span-1">
          <button
            type="button"
            id="locations-filter-btn"
            class="font-lato lg2:w-auto inline-flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            [ngClass]="
              getFilterButtonClass(
                locationsFilterFormControl,
                isLocationsDropdownOpen()
              )
            "
            [attr.aria-expanded]="isLocationsDropdownOpen()"
            aria-haspopup="listbox"
            aria-controls="locations-listbox"
            aria-label="Filter inventory by location"
            (click)="toggleLocationsDropdown($event)"
            (keydown.escape)="closeFilterDropdowns()"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >place</mat-icon
            >
            <span class="min-w-0 truncate">
              Location:
              <strong>{{
                getFilterLabel(locationsFilterFormControl, 'All')
              }}</strong>
            </span>
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isLocationsDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isLocationsDropdownOpen()) {
            <div
              id="locations-listbox"
              class="comcast-select-panel min-w-72"
              [class.comcast-select-panel--open]="isLocationsDropdownOpen()"
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby="locations-filter-btn"
              (click)="$event.stopPropagation()"
              (keydown.escape)="closeFilterDropdowns()"
            >
              <div class="comcast-select-options">
                @for (location of locationsFilterOptions; track location) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      isFilterSelected(locationsFilterFormControl, location)
                    "
                    role="option"
                    [attr.aria-selected]="
                      isFilterSelected(locationsFilterFormControl, location)
                    "
                    (click)="
                      toggleFilterValue(
                        locationsFilterFormControl,
                        location,
                        $event
                      )
                    "
                    (keydown.escape)="closeFilterDropdowns()"
                  >
                    {{ location }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Service -->
        <div class="relative min-w-0">
          <button
            type="button"
            id="service-filter-btn"
            class="font-lato lg2:w-auto inline-flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            [ngClass]="
              getFilterButtonClass(
                serviceFilterFormControl,
                isServiceDropdownOpen()
              )
            "
            [attr.aria-expanded]="isServiceDropdownOpen()"
            aria-haspopup="listbox"
            aria-controls="service-listbox"
            aria-label="Filter inventory by service type"
            (click)="toggleServiceDropdown($event)"
            (keydown.escape)="closeFilterDropdowns()"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >miscellaneous_services</mat-icon
            >
            <span class="min-w-0 truncate">
              Service:
              <strong>{{
                getFilterLabel(serviceFilterFormControl, 'All')
              }}</strong>
            </span>
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isServiceDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isServiceDropdownOpen()) {
            <div
              id="service-listbox"
              class="comcast-select-panel min-w-56"
              [class.comcast-select-panel--open]="isServiceDropdownOpen()"
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby="service-filter-btn"
              (click)="$event.stopPropagation()"
              (keydown.escape)="closeFilterDropdowns()"
            >
              <div class="comcast-select-options">
                @for (type of serviceFilterOptions; track type) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      isFilterSelected(serviceFilterFormControl, type)
                    "
                    role="option"
                    [attr.aria-selected]="
                      isFilterSelected(serviceFilterFormControl, type)
                    "
                    (click)="
                      toggleFilterValue(serviceFilterFormControl, type, $event)
                    "
                    (keydown.escape)="closeFilterDropdowns()"
                  >
                    {{ type }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Status -->
        <div class="relative min-w-0">
          <button
            type="button"
            id="status-filter-btn"
            class="font-lato lg2:w-auto inline-flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            [ngClass]="
              getFilterButtonClass(
                statusFilterFormControl,
                isStatusDropdownOpen()
              )
            "
            [attr.aria-expanded]="isStatusDropdownOpen()"
            aria-haspopup="listbox"
            aria-controls="status-listbox"
            aria-label="Filter inventory by status"
            (click)="toggleStatusDropdown($event)"
            (keydown.escape)="closeFilterDropdowns()"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >fact_check</mat-icon
            >
            <span class="min-w-0 truncate">
              Status:
              <strong>{{
                getFilterLabel(statusFilterFormControl, 'All')
              }}</strong>
            </span>
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isStatusDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isStatusDropdownOpen()) {
            <div
              id="status-listbox"
              class="comcast-select-panel min-w-48"
              [class.comcast-select-panel--open]="isStatusDropdownOpen()"
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby="status-filter-btn"
              (click)="$event.stopPropagation()"
              (keydown.escape)="closeFilterDropdowns()"
            >
              <div class="comcast-select-options">
                @for (status of statusFilterOptions; track status) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      isFilterSelected(statusFilterFormControl, status)
                    "
                    role="option"
                    [attr.aria-selected]="
                      isFilterSelected(statusFilterFormControl, status)
                    "
                    (click)="
                      toggleFilterValue(statusFilterFormControl, status, $event)
                    "
                    (keydown.escape)="closeFilterDropdowns()"
                  >
                    {{ status === 'New' ? 'Pending' : status }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Items per page -->
        <div class="relative min-w-0">
          <button
            type="button"
            id="inventory-page-size-btn"
            class="font-lato lg2:w-auto inline-flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            [ngClass]="{
              'border-blue-400 bg-white text-black': isPageSizeDropdownOpen(),
              'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50':
                !isPageSizeDropdownOpen(),
            }"
            [attr.aria-expanded]="isPageSizeDropdownOpen()"
            aria-haspopup="listbox"
            aria-controls="inventory-page-size-listbox"
            (click)="togglePageSizeDropdown($event)"
            (keydown.escape)="closePageSizeDropdown()"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
            >
              view_agenda
            </mat-icon>
            <span class="min-w-0 truncate">
              Per page: <strong>{{ pageSize }}</strong>
            </span>
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
            >
              {{
                isPageSizeDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}
            </mat-icon>
          </button>
          @if (isPageSizeDropdownOpen()) {
            <div
              id="inventory-page-size-listbox"
              class="comcast-select-panel min-w-48"
              [class.comcast-select-panel--open]="isPageSizeDropdownOpen()"
              role="listbox"
              aria-label="Items per page options"
              (click)="$event.stopPropagation()"
              (keydown.escape)="closePageSizeDropdown()"
            >
              <div class="comcast-select-options">
                @for (option of pageSizeOptions; track option) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      pageSize === option
                    "
                    role="option"
                    [attr.aria-selected]="pageSize === option"
                    (click)="
                      pageSizeControl.setValue(option);
                      page = 1;
                      closePageSizeDropdown()
                    "
                  >
                    {{ option }} items per page
                  </button>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <div
      class="text-brand-muted flex items-center justify-end gap-4 pb-4 text-xs"
    >
      <span>
        Showing {{ visibleServiceCount }} services
        @if (groupByFormControl.value) {
          • Grouped by {{ currentGroupByLabel }}
        }
        • Sort: {{ currentSortLabel }}
        @if (locationsFilterFormControl?.value?.length > 0) {
          • Location: {{ getFilterLabel(locationsFilterFormControl, 'All') }}
        }
        @if (serviceFilterFormControl?.value?.length > 0) {
          • Service: {{ getFilterLabel(serviceFilterFormControl, 'All') }}
        }
        @if (statusFilterFormControl?.value?.length > 0) {
          • Status: {{ getFilterLabel(statusFilterFormControl, 'All') }}
        }
      </span>
      @if (totalSelectedFilters > 0) {
        <button
          type="button"
          class="font-lato inline-flex shrink-0 cursor-pointer items-center gap-0.5 font-semibold text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          (click)="clearFilters()"
        >
          Clear all
          <mat-icon
            class="flex! h-3.5! w-3.5! items-center! justify-center! text-sm!"
            aria-hidden="true"
            >close</mat-icon
          >
        </button>
      }
    </div>
  }

  @if (isLoading || isServicesInProgress) {
    <div
      class="mt-4 space-y-2"
      role="status"
      aria-label="Loading inventory data"
      aria-busy="true"
    >
      @for (item of [1, 2, 3, 4]; track item) {
        <div
          class="animate-pulse overflow-hidden rounded-sm bg-white shadow-sm"
        >
          <!-- Skeleton header -->
          <div
            class="flex items-center justify-between border border-white px-6 py-4"
          >
            <div class="flex items-center gap-3">
              <div class="h-6 w-6 rounded-sm bg-gray-200"></div>
              <div class="space-y-2">
                <div class="h-4 w-32 rounded bg-gray-200"></div>
                <div class="bg-gray-150 h-3 w-24 rounded"></div>
              </div>
            </div>
            <div class="space-y-2 text-right">
              <div class="h-4 w-20 rounded bg-gray-200"></div>
              <div class="bg-gray-150 h-3 w-16 rounded"></div>
            </div>
          </div>
        </div>
      }
    </div>
  } @else if (groupedServices && hasObjectLength(groupedServices, 0)) {
    @if (services.length > 0) {
      <div
        class="border-gray-150 flex w-full flex-col items-center justify-center rounded-md border bg-white p-12 shadow-sm"
        role="status"
        aria-live="polite"
      >
        <div
          aria-hidden="true"
          class="custom-icon circuit_dark text-brand-muted w-4!"
        >
          service type icon
        </div>
        <h2 class="font-montserrat text-lg font-semibold">No services found</h2>
        <p class="font-lato text-brand-muted mt-1 max-w-sm text-center text-sm">
          There are no services based on your search. Try adjusting your filters
          or search criteria.
        </p>
      </div>
    } @else {
      <div
        class="border-gray-150 flex w-full flex-col items-center justify-center rounded-md border bg-white p-12 shadow-sm"
        role="status"
        aria-live="polite"
      >
        <mat-icon
          fontSet="material-icons-outlined"
          class="text-brand-muted mb-2 text-5xl!"
          aria-hidden="true"
          >storage</mat-icon
        >
        <h2 class="font-montserrat text-lg font-semibold">No services yet</h2>
        <p class="font-lato text-brand-muted mt-1 max-w-sm text-center text-sm">
          There are currently no services yet.
        </p>
      </div>
    }
  } @else {
    @if (groupedServices) {
      <div
        class="flex w-full flex-col gap-2 text-sm md:text-base"
        role="list"
        aria-label="Service groups"
      >
        @for (
          inventory of paginatedServices;
          track inventory.groupTitle;
          let groupIndex = $index
        ) {
          <app-accordion-item
            [expanded]="isGroupExpanded(inventory.groupTitle)"
            [ariaLabel]="
              (groupByFormControl.value === 'Z_Location__r.Name' &&
              inventory.services[0].CustomerLocationAlias
                ? inventory.services[0].CustomerLocationAlias + ', '
                : '') +
              inventory.groupTitle +
              ', ' +
              inventory.services.length +
              (inventory.services.length === 1 ? ' service' : ' services')
            "
            [panelId]="'inventory-group-panel-' + groupIndex"
            [headerId]="'inventory-group-header-' + groupIndex"
            (toggle)="
              toggleGroup(inventory.groupTitle);
              onHeaderClick(inventory.services[0])
            "
          >
            <div
              accordion-icon
              class="custom-icon hidden shrink-0 md:block"
              aria-hidden="true"
              [ngClass]="{
                location_dark:
                  groupByFormControl.value === 'Z_Location__r.Name',
                circuit_dark: groupByFormControl.value === 'RecordType.Name',
                services_icon: groupByFormControl.value === 'Status__c',
              }"
            >
              group icon
            </div>

            <div accordion-label>
              <span class="font-semibold">{{ inventory.groupTitle }}</span>
              @if (
                groupByFormControl.value === 'Z_Location__r.Name' &&
                inventory.services[0].CustomerLocationAlias
              ) {
                <div class="capitalize">
                  {{ inventory.services[0].CustomerLocationAlias }}
                </div>
              }
            </div>

            <div accordion-meta>
              <div class="text-right font-semibold">
                {{ inventory.services.length }}
                <span class="hidden md:inline">service(s)</span>
              </div>
              <div class="hidden text-black md:block">
                {{ inventory.totalMrc | currency
                }}<span class="text-brand-muted">/mo</span>
              </div>
            </div>

            @for (
              service of inventory.services;
              track service.Name;
              let i = $index
            ) {
              <ng-template #serviceRowContent let-service="service">
                @if (service === selectedService) {
                  <div
                    class="absolute inset-y-0 left-0 hidden w-0.75 bg-blue-700 md:block"
                  ></div>
                }
                <div
                  class="flex flex-col px-2 py-3 text-xs md:flex-row md:justify-between md:p-4 md:text-sm"
                >
                  <div>
                    <div class="pl-2">
                      <div class="flex items-center font-semibold">
                        @if (
                          groupByFormControl.value === 'Z_Location__r.Name'
                        ) {
                          <mat-icon
                            fontSet="material-icons-outlined"
                            class="mr-1 hidden! h-4! w-4! items-center! justify-center! text-base! text-black! md:flex!"
                            aria-hidden="true"
                            >router</mat-icon
                          >
                          <div class="text-black uppercase">
                            {{ service.Name | uppercase }}
                          </div>
                        }
                        @if (
                          groupByFormControl.value !== 'Z_Location__r.Name'
                        ) {
                          <mat-icon
                            fontSet="material-icons-outlined"
                            class="mr-1 hidden! h-4! w-4! items-center! justify-center! text-base! text-black! md:flex!"
                            aria-hidden="true"
                            >place</mat-icon
                          >
                          <div class="text-black uppercase">
                            {{ service.Z_Location__r.Name }}
                          </div>
                        }
                      </div>
                      @if (
                        groupByFormControl.value !== 'Z_Location__r.Name' &&
                        service.CustomerLocationAlias
                      ) {
                        <div class="mt-0.5 text-black capitalize md:ml-5">
                          {{ service.CustomerLocationAlias }}
                        </div>
                      }
                      @if (groupByFormControl.value === 'Z_Location__r.Name') {
                        <div
                          class="text-brand-muted mt-0.5 text-xs uppercase md:ml-5"
                        >
                          {{ service.RecordType.Name | uppercase }}
                          @if (service.Bandwidth_Downstream__c) {
                            - {{ service.Bandwidth_Downstream__c }}
                          }
                          @if (service.Asymmetrical_Bandwidth__c) {
                            / {{ service.Bandwidth_Upstream__c }}
                          }
                          @if (service.Vendor__r) {
                            -
                            {{
                              service.Vendor__r.Display_Alias__c
                                ? service.Vendor__r.Display_Alias__c
                                : service.Vendor__r.Name
                            }}
                          }
                        </div>
                      }
                      @if (groupByFormControl.value !== 'Z_Location__r.Name') {
                        <div>
                          <div class="mt-1.25 flex items-center">
                            <mat-icon
                              fontSet="material-icons-outlined"
                              class="mr-1 hidden! h-4! w-4! items-center! justify-center! text-base! text-black! md:flex!"
                              aria-hidden="true"
                              >router</mat-icon
                            >

                            <div class="text-black uppercase">
                              {{ service.Name | uppercase }}
                              @if (
                                groupByFormControl.value !== 'RecordType.Name'
                              ) {
                                - {{ service.RecordType.Name | uppercase }}
                              }
                              @if (service.Bandwidth_Downstream__c) {
                                - {{ service.Bandwidth_Downstream__c }}
                              }
                              @if (service.Asymmetrical_Bandwidth__c) {
                                / {{ service.Bandwidth_Upstream__c }}
                              }
                              @if (service.Vendor__r) {
                                -
                                {{
                                  service.Vendor__r.Display_Alias__c
                                    ? service.Vendor__r.Display_Alias__c
                                    : service.Vendor__r.Name
                                }}
                              }
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </div>

                  <div
                    class="mt-4 flex justify-between md:mt-0 md:flex-col md:justify-start"
                  >
                    <div
                      class="mb-2 flex items-center justify-start md:justify-end"
                    >
                      <span
                        class="comcast-status-pill ml-1"
                        [ngClass]="{
                          'comcast-status-pill--success':
                            service.Status__c === 'In Service',
                          'comcast-status-pill--warning':
                            service.Status__c === 'New' ||
                            service.Status__c === 'Pending' ||
                            service.Status__c === 'In Progress',
                          'comcast-status-pill--danger':
                            service.Status__c === 'Canceled' ||
                            service.Status__c === 'Disconnected',
                        }"
                      >
                        {{
                          service.Status__c === 'New'
                            ? 'Pending'
                            : service.Status__c
                        }}
                      </span>
                    </div>

                    <div class="self-center text-black md:text-right">
                      <span class="sr-only">Monthly charge:</span>
                      {{
                        service.MRR__c
                          ? (service.MRR__c | currency)
                          : (0 | currency)
                      }}<span class="text-brand-muted!">/mo</span>
                    </div>
                  </div>
                </div>
              </ng-template>

              <div role="listitem">
                <a
                  [routerLink]="['/services', service.Id]"
                  class="hover:bg-blue-75/50 relative block cursor-pointer border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset md:hidden"
                  [attr.aria-label]="
                    'View service: ' +
                    service.Name +
                    ', status: ' +
                    (service.Status__c === 'New'
                      ? 'Pending'
                      : service.Status__c)
                  "
                >
                  <ng-container
                    [ngTemplateOutlet]="serviceRowContent"
                    [ngTemplateOutletContext]="{ service: service }"
                  ></ng-container>
                </a>

                <div
                  tabindex="0"
                  role="button"
                  class="hover:bg-blue-75/50 relative hidden cursor-pointer border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset md:block"
                  [class.bg-blue-75]="service === selectedService"
                  (click)="onSelectService(service)"
                  (keyup.enter)="onSelectService(service)"
                  (keyup.space)="onSelectService(service)"
                  (keydown.space)="$event.preventDefault()"
                  [attr.aria-pressed]="service === selectedService"
                  [attr.aria-label]="
                    'View service: ' +
                    service.Name +
                    ', status: ' +
                    (service.Status__c === 'New'
                      ? 'Pending'
                      : service.Status__c)
                  "
                >
                  <ng-container
                    [ngTemplateOutlet]="serviceRowContent"
                    [ngTemplateOutletContext]="{ service: service }"
                  ></ng-container>
                </div>
              </div>
            }
          </app-accordion-item>
        }
      </div>
      <div class="flex w-full justify-center py-6">
        <app-pagination
          [currentPage]="page"
          [totalPages]="totalPages"
          [itemsPerPage]="pageSize"
          (pageChange)="onPageChange($event)"
        ></app-pagination>
      </div>
    }
  }
</section>
